import { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 5;
`;

const GlobalTopoBackground = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const animFrameRef = useRef(null);
  const timeRef = useRef(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const t = timeRef.current;
    const mx = mouseRef.current.x;
    const my = mouseRef.current.y;

    ctx.clearRect(0, 0, W, H);

    const numLines = 22;
    const spacing = H / (numLines + 1);

    for (let i = 1; i <= numLines; i++) {
      const baseY = i * spacing;

      // Opacity peaks in the mid-range lines — subtle at the edges
      const alpha = 0.045 + 0.075 * Math.sin((i / numLines) * Math.PI);

      ctx.beginPath();
      ctx.strokeStyle = `rgba(180, 175, 220, ${alpha})`;
      ctx.lineWidth = 1.2;

      const step = 3;
      for (let x = 0; x <= W; x += step) {
        // Mouse warp — magnetic pull toward cursor
        const dx = x - mx;
        const dy = baseY - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const influence = Math.max(0, 1 - dist / 300);
        const warpY = -42 * influence * influence * (1 - dist / 300);

        // Multi-frequency layered waves for organic topographic feel
        const wave =
          Math.sin(x * 0.008 + t * 0.38 + i * 0.55) * 24 +
          Math.sin(x * 0.014 - t * 0.22 + i * 1.2) * 15 +
          Math.sin(x * 0.004 + t * 0.17 + i * 0.28) * 32 +
          Math.cos(x * 0.011 + t * 0.30 - i * 0.65) * 11 +
          Math.sin(x * 0.022 + t * 0.50 + i * 0.9) * 7;

        const y = baseY + wave + warpY;

        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    timeRef.current += 0.011;
    animFrameRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resize();
    window.addEventListener('resize', resize);

    const onMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const onMouseLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 };
    };

    window.addEventListener('mousemove', onMouseMove);
    document.documentElement.addEventListener('mouseleave', onMouseLeave);

    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [draw]);

  return <Canvas ref={canvasRef} aria-hidden="true" />;
};

export default GlobalTopoBackground;
